import type { PortfolioReport } from '@interfaces/portfolioReport';
import type {
  PortfolioAdvisorInsights,
  PortfolioTaxInsight,
  TurnoverBreakdown,
} from '../../types/advisorInsights.types';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import {
  isFundingSegregationNotApplied,
  isInactiveZeroAccountDecision,
} from '../audit/decisionPredicates';
import { isRegisteredAccountType } from '../presentation/isRegisteredAccountType';
import {
  OptimizerDecisionImpact,
  OptimizerDecisionStage,
} from './advisorSituationKeys';
import {
  countEstimatedGainLines,
  countTaxableSellLines,
  estimateTaxableRealizedGainCents,
} from './estimateTaxableRealizedGainCents';
import {
  TURNOVER_BREAKDOWN_PATTERN,
  TURNOVER_PCT_PATTERN,
} from './optimizerDecisionActionPatterns';

export type {
  PortfolioAdvisorDecisionInsight,
  PortfolioAdvisorInsights,
  PortfolioTaxInsight,
  TurnoverBreakdown,
} from '../../types/advisorInsights.types';

const MATERIAL_IMPACTS = new Set<string>([
  OptimizerDecisionImpact.Material,
  OptimizerDecisionImpact.Blocking,
]);

export const parseTurnoverBreakdown = (
  action?: string | null,
): TurnoverBreakdown | null => {
  if (!action) {
    return null;
  }

  const match = action.match(TURNOVER_BREAKDOWN_PATTERN);
  if (!match) {
    return null;
  }

  const [, turnoverPct, buyPct, sellPct, holdingCount] = match;
  const parsed = {
    turnoverPct: Number.parseFloat(turnoverPct),
    buyPct: Number.parseFloat(buyPct),
    sellPct: Number.parseFloat(sellPct),
    holdingCount: Number.parseInt(holdingCount, 10),
  };

  return Object.values(parsed).every((value) => Number.isFinite(value))
    ? parsed
    : null;
};

export const parseTurnoverPct = (action?: string | null): number | null => {
  if (!action) {
    return null;
  }

  const match = action.match(TURNOVER_PCT_PATTERN);
  if (!match) {
    return null;
  }

  const parsed = Number.parseFloat(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

export const buildPortfolioTaxInsight = (
  audit: OptimizerAuditV2Record | undefined,
): PortfolioTaxInsight | null => {
  if (!audit) {
    return null;
  }

  let registeredSellCents = 0;
  let taxableSellCents = 0;

  for (const subAccount of audit.subAccountResults ?? []) {
    const registered = isRegisteredAccountType(subAccount.accountType);

    for (const diff of subAccount.weightDiffs ?? []) {
      const deltaCents = diff.deltaCents ?? 0;
      if (deltaCents >= 0) {
        continue;
      }

      const sellCents = Math.abs(deltaCents);
      if (registered) {
        registeredSellCents += sellCents;
      } else {
        taxableSellCents += sellCents;
      }
    }
  }

  const totalSellCents = registeredSellCents + taxableSellCents;
  if (totalSellCents <= 0) {
    return null;
  }

  return {
    registeredSellCents,
    taxableSellCents,
    registeredSellPct: (registeredSellCents / totalSellCents) * 100,
    hasSells: true,
    estRealizedGainCents: null,
    estRealizedGainIsComplete: false,
    incompleteGainLineCount: 0,
  };
};

const attachRealizedGainEstimate = (
  taxInsight: PortfolioTaxInsight,
  audit: OptimizerAuditV2Record,
  report?: PortfolioReport,
): PortfolioTaxInsight => {
  if (taxInsight.taxableSellCents <= 0) {
    return taxInsight;
  }

  if (!report) {
    return {
      ...taxInsight,
      incompleteGainLineCount: countTaxableSellLines(audit),
    };
  }

  const estimate = estimateTaxableRealizedGainCents(audit, report);
  if (!estimate) {
    return {
      ...taxInsight,
      incompleteGainLineCount: countTaxableSellLines(audit),
    };
  }

  const estimatedGainLineCount = countEstimatedGainLines(estimate);
  if (estimatedGainLineCount <= 0) {
    return {
      ...taxInsight,
      incompleteGainLineCount: estimate.incompleteLineCount,
    };
  }

  return {
    ...taxInsight,
    estRealizedGainCents: estimate.estRealizedGainCents,
    estRealizedGainIsComplete: estimate.isComplete,
    incompleteGainLineCount: estimate.incompleteLineCount,
  };
};

export const buildPortfolioAdvisorInsights = (
  audit: OptimizerAuditV2Record | undefined,
  report?: PortfolioReport,
): PortfolioAdvisorInsights | null => {
  if (!audit) {
    return null;
  }

  const hasValidationFailure =
    audit.passed === false || (audit.validationErrors?.length ?? 0) > 0;

  const baseTaxInsight = buildPortfolioTaxInsight(audit);
  const taxInsight = baseTaxInsight
    ? attachRealizedGainEstimate(baseTaxInsight, audit, report)
    : null;
  const turnoverDecision = (audit.decisions ?? []).find(
    (decision) => decision.stage === OptimizerDecisionStage.Turnover,
  );
  const turnoverPct = parseTurnoverPct(turnoverDecision?.action);
  const turnoverLabel = turnoverDecision?.action ?? null;
  const turnoverBreakdown = parseTurnoverBreakdown(turnoverDecision?.action);

  const decisions = (audit.decisions ?? [])
    .filter(
      (decision) => decision.impact && MATERIAL_IMPACTS.has(decision.impact),
    )
    .filter((decision) => decision.stage !== OptimizerDecisionStage.Turnover)
    .filter(
      (decision) =>
        !(
          hasValidationFailure &&
          decision.stage === OptimizerDecisionStage.InvariantCheck
        ),
    )
    .filter((decision) => !isInactiveZeroAccountDecision(decision))
    .filter((decision) => !isFundingSegregationNotApplied(decision))
    .map((decision) => ({
      stage: decision.stage,
      action: decision.action,
      impact: decision.impact ?? OptimizerDecisionImpact.Material,
    }));

  if (!taxInsight && turnoverPct == null && decisions.length === 0) {
    return null;
  }

  return {
    taxInsight,
    turnoverPct,
    turnoverLabel,
    turnoverBreakdown,
    decisions,
  };
};
